import { useQuery } from '@apollo/client';
import { Button, Icon, List, ListItem, Spinner } from '@sixfold/common-ui';
import React, { useState } from 'react';
import JsonView from 'react18-json-view';
import 'react18-json-view/src/style.css';

import { FormattedDate } from '../../components/date_formatting/formatted_date';
import {
  LatestPlatformCallbacksQuery as LatestPlatformCallbacksQueryResult,
  LatestPlatformCallbacksQueryVariables,
} from '../../lib/graphql';
import { PlatformCallback, Tour } from '../entities';
import { latestPlatformCallbacksQuery } from '../graphql';

const PlatformCallbackComponent: React.FunctionComponent<{ callback: PlatformCallback }> = ({ callback }) => {
  const [showsPayload, setShowsPayload] = useState<boolean>(false);
  const { platform, subjectId, subjectType, createdAt } = callback;

  return (
    <table className="ui-table compact-table">
      <thead>
        <tr>
          <th>
            {platform?.platform_name ?? 'Unknown platform'} - {subjectType}:{subjectId} -{' '}
            <FormattedDate date={createdAt} />
            <Button size="small" onClick={() => setShowsPayload(!showsPayload)} style={{ float: 'right' }}>
              {showsPayload ? 'Hide payload' : 'Show payload'}
            </Button>
          </th>
        </tr>
      </thead>
      <tbody>
        {showsPayload && (
          <tr>
            <td>
              <div
                style={{
                  wordBreak: 'break-word',
                  whiteSpace: 'pre-wrap',
                  fontFamily: 'monospace',
                  backgroundColor: 'var(--color-neutral-bg-surface-sunken)',
                  padding: '8px',
                }}>
                <JsonView src={callback.payload} enableClipboard={true} />
              </div>
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
};

export const PlatformCallbackList: React.FunctionComponent<{
  tour: Pick<Tour, 'isSendEtaToPlatformEnabled' | 'tour_id'>;
}> = ({ tour: { isSendEtaToPlatformEnabled, tour_id } }) => {
  const { loading, data, error } = useQuery<LatestPlatformCallbacksQueryResult, LatestPlatformCallbacksQueryVariables>(
    latestPlatformCallbacksQuery,
    {
      variables: { tour_id },
      fetchPolicy: 'network-only',
    },
  );

  return (
    <>
      <List direction="row">
        <ListItem className={'tour__feature__flag'}>
          <h3>Latest callbacks</h3>
        </ListItem>
        {!isSendEtaToPlatformEnabled && (
          <>
            <ListItem>
              <Icon name="small-alert" kind="warning" />
            </ListItem>
            <ListItem className={'tour__feature__flag'}>Sending ETA to platform not enabled!</ListItem>
          </>
        )}
      </List>
      {(() => {
        if (loading) {
          return (
            <table className="ui-table compact-table">
              <tbody>
                <tr style={{ textAlign: 'center' }}>
                  <td>
                    <Spinner size="medium" />
                  </td>
                </tr>
              </tbody>
            </table>
          );
        }

        if (error) {
          return (
            <table className="ui-table compact-table">
              <tbody>
                <tr style={{ textAlign: 'center' }}>
                  <td>Error loading callbacks: {error.message}</td>
                </tr>
              </tbody>
            </table>
          );
        }

        const platformCallbacks = data?.tour?.platformCallbacksV2 ?? [];

        if (platformCallbacks.length === 0) {
          return (
            <table className="ui-table compact-table">
              <tbody>
                <tr style={{ textAlign: 'center' }}>
                  <td>No callbacks</td>
                </tr>
              </tbody>
            </table>
          );
        }

        return (
          <>
            {platformCallbacks.map((callback) => (
              <PlatformCallbackComponent
                key={`${callback.platform?.platform_id}:${callback.subjectType}:${callback.subjectId}`}
                callback={callback}
              />
            ))}
          </>
        );
      })()}
    </>
  );
};
